/**
 * Shared full-density timeline primitives used by DayView and WeekView.
 *
 * A fixed pixel height per hour (ROW_HEIGHT) keeps the time gutter and every
 * day column vertically aligned. Events are absolutely positioned by pixel
 * offset (with a minimum height so very short events stay legible) and render
 * via the height-aware `TimelineEvent`. The gutter can show extra timezone
 * columns; `TimelineGutterHeader` labels them.
 */

import { cn } from '@/lib/cn';
import { TimelineEvent } from './TimelineEvent';
import {
  hourRows,
  layoutDay,
  formatHour,
  formatHourInZone,
  formatTime,
  timeOffsetPct,
  type TimeBounds,
  type GutterColumnSpec,
} from './calendar.helpers';
import type { NormalizedEvent } from './calendar.types';

export const ROW_HEIGHT = 60; // px per hour
export const GUTTER_COL_W = 54; // px per gutter (timezone) column
const MIN_EVENT_PX = 22; // floor so 15-min events still show their time

export function timelineHeight(bounds: TimeBounds): number {
  return (bounds.endHour - bounds.startHour) * ROW_HEIGHT;
}

export function gutterWidth(columns: GutterColumnSpec[]): number {
  return columns.length * GUTTER_COL_W;
}

// ─── Gutter ─────────────────────────────────────────────────────────────────

function GutterColumn({
  rows,
  day,
  bounds,
  hour12,
  tz,
}: {
  rows: number[];
  day: Date;
  bounds: TimeBounds;
  hour12?: boolean;
  tz?: string;
}) {
  return (
    <div className="relative shrink-0" style={{ width: GUTTER_COL_W, height: timelineHeight(bounds) }}>
      {rows.map((h, i) => (
        <div
          key={h}
          className="absolute inset-x-0 -translate-y-1/2 whitespace-nowrap text-center text-[11px] tabular-nums text-muted-foreground"
          style={{ top: i * ROW_HEIGHT }}
        >
          {tz ? formatHourInZone(day, h, tz, hour12) : formatHour(h, hour12)}
        </div>
      ))}
    </div>
  );
}

/** Left-hand hour labels — one column per displayed timezone. */
export function TimelineGutter({
  columns,
  day,
  bounds,
  hour12,
}: {
  columns: GutterColumnSpec[];
  day: Date;
  bounds: TimeBounds;
  hour12?: boolean;
}) {
  const rows = hourRows(bounds);
  return (
    <div className="flex shrink-0 select-none" aria-hidden>
      {columns.map((c, idx) => (
        <GutterColumn key={idx} rows={rows} day={day} bounds={bounds} hour12={hour12} tz={c.tz} />
      ))}
    </div>
  );
}

/** Header row of timezone labels, widths matching the gutter columns. */
export function TimelineGutterHeader({ columns }: { columns: GutterColumnSpec[] }) {
  return (
    <div className="flex shrink-0 select-none pt-2" aria-hidden>
      {columns.map((c, idx) => (
        <div
          key={idx}
          className="overflow-hidden truncate text-center text-[11px] font-medium text-muted-foreground/80"
          style={{ width: GUTTER_COL_W }}
        >
          {c.headerLabel}
        </div>
      ))}
    </div>
  );
}

// ─── Day column ─────────────────────────────────────────────────────────────

function Gridlines({ bounds }: { bounds: TimeBounds }) {
  const rows = hourRows(bounds);
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      {rows.map((h, i) => (
        <div
          key={h}
          className="absolute inset-x-0 border-t border-dashed border-border/70"
          style={{ top: i * ROW_HEIGHT }}
        />
      ))}
    </div>
  );
}

export interface TimelineDayColumnProps {
  day?: Date;
  events: NormalizedEvent[];
  bounds: TimeBounds;
  hour12?: boolean;
  className?: string;
}

export function TimelineDayColumn({ events, bounds, hour12, className }: TimelineDayColumnProps) {
  const positioned = layoutDay(events, bounds);
  const totalHeight = timelineHeight(bounds);

  return (
    <div className={cn('relative min-w-0 flex-1', className)} style={{ height: totalHeight }}>
      <Gridlines bounds={bounds} />

      {positioned.map((p) => {
        const widthPct = 100 / p.colCount;
        const gap = p.colCount > 1 ? 2 : 0; // px gutter between overlap columns
        const topPx = (p.top / 100) * totalHeight;
        const heightPx = Math.max((p.height / 100) * totalHeight, MIN_EVENT_PX);
        return (
          <div
            key={p.event.id}
            className="absolute p-px"
            style={{
              top: topPx,
              height: heightPx,
              left: `calc(${p.col * widthPct}% + ${gap / 2}px)`,
              width: `calc(${widthPct}% - ${gap}px)`,
            }}
          >
            <TimelineEvent
              event={p.event}
              heightPx={heightPx - 2}
              narrow={p.colCount > 1}
              hour12={hour12}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The live red "current time" line. Spans the day columns at the current
 * time's offset, with a time pill in the gutter on the left. Rendered once
 * per timeline track (not per column) and only when today is visible.
 */
export function NowIndicator({
  bounds,
  now,
  gutterW,
  hour12,
}: {
  bounds: TimeBounds;
  now: Date;
  gutterW: number;
  hour12?: boolean;
}) {
  const pct = timeOffsetPct(bounds, now);
  if (pct === null) return null;
  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-20 flex items-center"
      style={{ top: `${pct}%` }}
      aria-label="Current time"
    >
      {/* Pill centered in the primary (local) gutter column so it lines up
          with the centered hour labels above/below it. */}
      <div className="flex shrink-0" style={{ width: gutterW }}>
        <div className="shrink-0" style={{ width: gutterW - GUTTER_COL_W }} />
        <div className="flex shrink-0 justify-center" style={{ width: GUTTER_COL_W }}>
          <span className="whitespace-nowrap rounded bg-red-700 px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums text-white shadow-sm">
            {formatTime(now, hour12)}
          </span>
        </div>
      </div>
      <span className="-ml-1 size-2 shrink-0 rounded-full bg-red-700" />
      <span className="h-px flex-1 bg-red-700" />
    </div>
  );
}
